import { useEffect, useRef, useState } from "react"
import _ from "lodash"
import moment from "moment"

import {
  getDistribuitorProviderDailyAvailable,
  getProviderDetail,
  getProviders,
  postDistribuitorProviderDaily,
  postSubRegions,
  putDistribuitorProviderDaily,
} from "helpers/backend_helper"

import { SAVED_FEEDBACK_MS } from "./static"

/**
 * Ordena la lista plana de zonas dejando cada subzona debajo de su zona padre.
 */
export const ordenarRegiones = regions => {
  const padres = (regions || []).filter(region => !region.parent_id)
  const ordenados = []

  _.orderBy(padres, ["name"]).forEach(padre => {
    ordenados.push(padre)
    _.orderBy(
      (regions || []).filter(region => region.parent_id === padre.id),
      ["name"]
    ).forEach(hija => ordenados.push(hija))
  })

  // zonas huérfanas (su padre no está en la lista) al final, para no perderlas
  ;(regions || []).forEach(region => {
    if (!ordenados.some(item => item.id === region.id)) ordenados.push(region)
  })

  return ordenados
}

export const etiquetaRegion = (region, regions) => {
  if (!region.parent_id) return region.name
  const padre = (regions || []).find(item => item.id === region.parent_id)
  return padre ? `${padre.name} | ${region.name}` : region.name
}

export default function useAvailabilityCreate() {
  const [isLoading, setIsLoading] = useState(true)

  const [providers, setProviders] = useState([])
  const [providerId, setProviderId] = useState(null)

  const [dates, setDates] = useState([])
  const [date, setDate] = useState(null)

  const [farms, setFarms] = useState([])
  const [farmId, setFarmId] = useState(null)

  const [products, setProducts] = useState([])
  const [tabActive, setTabActive] = useState(0)

  const [regions, setRegions] = useState([])
  const [dailies, setDailies] = useState([])

  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const savedTimer = useRef(null)

  const [expanded, setExpanded] = useState(null)
  const [editing, setEditing] = useState(null)

  useEffect(() => {
    const cargarProveedores = async () => {
      try {
        const response = await getProviders()
        setProviders(response || [])
        if (response && response.length) setProviderId(response[0].id)
        else setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
      }
    }
    cargarProveedores()

    return () => clearTimeout(savedTimer.current)
  }, [])

  useEffect(() => {
    if (!providerId) return

    const cargarProveedor = async () => {
      setIsLoading(true)
      try {
        const [disponibilidad, detalle] = await Promise.all([
          getDistribuitorProviderDailyAvailable(providerId),
          getProviderDetail(providerId),
        ])

        const listaFechas = disponibilidad?.dates || []
        const hoy = moment().format("YYYY-MM-DD")
        setDates(listaFechas)
        setDate(
          listaFechas.includes(hoy) ? hoy : listaFechas[0] || null
        )
        setDailies(disponibilidad?.data || [])

        const listaFarms = detalle?.farms || []
        setFarms(listaFarms)
        seleccionarFarm(listaFarms[0] || null)
      } catch (error) {
        setDates([])
        setDailies([])
        setFarms([])
        seleccionarFarm(null)
      }
      setIsLoading(false)
    }
    cargarProveedor()
    // eslint-disable-next-line
  }, [providerId])

  const seleccionarFarm = farm => {
    setFarmId(farm ? farm.id : null)
    setRegions(farm ? ordenarRegiones(farm.regions) : [])
    setProducts(farm ? _.orderBy(farm.products || [], ["position"], ["asc"]) : [])
    setTabActive(0)
    setExpanded(null)
    setEditing(null)
  }

  const farm = farms.find(item => item.id === farmId) || null
  const product = products[tabActive] || null

  const registradas = dailies.filter(
    daily =>
      product &&
      Number(daily.farm_id) === Number(farmId) &&
      Number(daily.product_id) === Number(product.id) &&
      daily.date === date
  )

  const onChangeProvider = value => {
    setProviderId(value)
    setExpanded(null)
    setEditing(null)
  }

  const onChangeDate = value => {
    setDate(value)
    setExpanded(null)
    setEditing(null)
  }

  const onChangeFarm = value =>
    seleccionarFarm(farms.find(item => item.id === value) || null)

  const onChangeTab = index => {
    setTabActive(index)
    setExpanded(null)
    setEditing(null)
  }

  const onToggleExpanded = id =>
    setExpanded(previo => (previo === id ? null : id))

  const onStartEdit = id => {
    setEditing(id)
    setExpanded(id)
  }

  const onCancelEdit = () => setEditing(null)

  const marcarGuardado = () => {
    setIsSaved(true)
    clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => setIsSaved(false), SAVED_FEEDBACK_MS)
  }

  /** Mezcla en la lista los registros devueltos por el servidor. */
  const absorber = response => {
    const devueltos = Array.isArray(response) ? response : [response]
    setDailies(previo => {
      const siguiente = [...previo]
      devueltos.forEach(item => {
        const index = siguiente.findIndex(daily => daily.id === item.id)
        if (index >= 0) siguiente[index] = item
        else siguiente.push(item)
      })
      return siguiente
    })
  }

  /** Alta. Devuelve true si el servidor aceptó el registro. */
  const onCreate = async registro => {
    if (isSaving) return false
    setIsSaving(true)
    let ok = false
    try {
      const response = await postDistribuitorProviderDaily({
        providerId,
        date,
        data: [registro],
      })
      absorber(response)
      marcarGuardado()
      ok = true
    } catch (error) {
      // api_helper ya notifica el error
    }
    setIsSaving(false)
    return ok
  }

  /** Edición inline. Devuelve true si el servidor aceptó los cambios. */
  const onUpdate = async registro => {
    if (isSaving) return false
    setIsSaving(true)
    let ok = false
    try {
      const response = await putDistribuitorProviderDaily({
        providerId,
        date,
        daily_availability_id: registro.id,
        data: [registro],
      })
      absorber(response)
      setEditing(null)
      ok = true
    } catch (error) {
      // api_helper ya notifica el error
    }
    setIsSaving(false)
    return ok
  }

  /**
   * Guarda las sub-zonas y refresca la sucursal. Devuelve la lista de zonas ya
   * actualizada para que quien llamó pueda emparejar los precios capturados
   * con el id definitivo de cada sub-zona nueva.
   */
  const onSaveZones = async subzonas => {
    if (!farm) return []
    const response = await postSubRegions({ regions: subzonas, farm })
    const actualizadas = response || []
    setRegions(ordenarRegiones(actualizadas))
    setFarms(previo =>
      previo.map(item =>
        item.id === farm.id ? { ...item, regions: actualizadas } : item
      )
    )
    return actualizadas
  }

  return {
    state: {
      isLoading,
      providers,
      providerId,
      dates,
      date,
      farms,
      farmId,
      farm,
      products,
      tabActive,
      product,
      regions,
      registradas,
      isSaving,
      isSaved,
      expanded,
      editing,
    },
    events: {
      onChangeProvider,
      onChangeDate,
      onChangeFarm,
      onChangeTab,
      onToggleExpanded,
      onStartEdit,
      onCancelEdit,
      onCreate,
      onUpdate,
      onSaveZones,
    },
  }
}
